// Browser-only runtime helpers used by the SDK.

let cachedSessionId: string | undefined;

/** Returns a cryptographically random UUID-ish string. */
export function uuid(): string {
  // Use crypto.randomUUID if available, otherwise fall back to Math.random.
  /* istanbul ignore else */
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  /* istanbul ignore next */
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Returns a stable per-tab session id, stored in sessionStorage. */
export function sessionId(): string {
  if (cachedSessionId) {
    return cachedSessionId;
  }
  try {
    const key = '__mpwa_sid';
    const existing = sessionStorage.getItem(key);
    if (existing) {
      cachedSessionId = existing;
      return existing;
    }
    const fresh = uuid();
    sessionStorage.setItem(key, fresh);
    cachedSessionId = fresh;
    return fresh;
  } catch {
    // Storage disabled (private mode). Per-tab in-memory fallback.
    cachedSessionId ??= uuid();
    return cachedSessionId;
  }
}

/** Returns the rough device class based on user-agent hints. */
export function deviceType(): 'desktop' | 'mobile' | 'tablet' | 'ctv' {
  const ua = navigator.userAgent.toLowerCase();
  if (/(ipad|tablet)/i.test(ua)) {
    return 'tablet';
  }
  if (/(mobile|iphone|android)/i.test(ua)) {
    return 'mobile';
  }
  if (/(smarttv|appletv|googletv)/i.test(ua)) {
    return 'ctv';
  }
  return 'desktop';
}
